import { isSafeReturnPath, landingBaseUrl, moduleDeepLinkUrl } from './module-links';

// Shared notification row + the read/follow bookkeeping used by both the sidebar
// popover and the full inbox view, so the two surfaces cannot drift apart.
//
// The WRITES themselves deliberately do NOT live here any more (issue 195,
// decision D5). A write goes through `useApiMutation`, which needs a setup
// context, owns the pending state and the failure channel, and takes its tier
// from the surface the user is looking at — none of which a plain module
// function has. So each view holds its own seam call, and this module keeps only
// what is genuinely shared: the row type, the cross-component signal, and the
// two pure functions that say what a successful write means.
export interface Notification {
  id: string;
  sender: string;
  text: string;
  ctaConstant: string | null;
  ctaContext?: Record<string, unknown> | null;
  readAt: string | null;
  createdAt: string;
}

// Cross-component signal: any read-state change dispatches this so the shell can
// refresh its unread badge. One channel only (no per-component Vue emit) — the
// shell listens once.
export function notifyNotificationsChanged(): void {
  window.dispatchEvent(new Event('lumi:notifications-changed'));
}

/** Is this row already in the state the click is asking for? */
export function readStateMatches(n: Notification, read: boolean): boolean {
  return !!n.readAt === read;
}

/** What a successful read/unread write means for the row on screen. */
export function applyReadState(n: Notification, read: boolean): void {
  n.readAt = read ? new Date().toISOString() : null;
  notifyNotificationsChanged();
}

/**
 * The owning module's resolved deep link for a followed notification, via the
 * cross-module SSO handoff (support tickets resolve to the workspace's own
 * origin). Null when nothing resolves — the caller then stays put.
 *
 * `user` is the historical module key of the tenant administration surface, and
 * the consented support-access review link (#426, ADR 0041) still carries it. Those
 * pages are routes of THIS app since #432, so the link is followed in place — no
 * cross-app hop, no session handoff, nothing to build a URL out of.
 *
 * `landing` is the public marketing site (issue 576: the in-app-only contract
 * notice links the announced Terms/AVV version there). It is a public page, so
 * the link carries NO session handoff — tokens must never ride to it — and the
 * deep link must be a root-relative path, like every resolved deep link.
 */
export function followTargetUrl(n: Notification, payload: unknown): string | null {
  const cta = (payload as { callToAction?: { module?: string; deepLink?: string } } | null)?.callToAction;
  if (!cta?.deepLink) return null;
  const moduleKey = cta.module || n.sender;
  if (moduleKey === 'user' || moduleKey === 'admin') return cta.deepLink;
  if (moduleKey === 'landing') {
    const base = landingBaseUrl().replace(/\/+$/, '');
    return base && isSafeReturnPath(cta.deepLink) ? `${base}${cta.deepLink}` : null;
  }
  return moduleDeepLinkUrl(moduleKey, cta.deepLink) || null;
}
